import addressApi from 'api/address';
import marketApi from 'api/marketApi';
import { COUNTRIES_API_PROVIDE } from 'store/address/reducer';
import AddImages from 'components/AddImages';
import FormField from 'components/AddLocation/FormField';
import Button from 'components/App/Button';
import InputValidation from 'components/App/InputValidation';
import SelectValidation from 'components/App/SelectValidation';
import { useFormik } from 'formik';
import { Pencil } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Card, Col, FormFeedback, Input, Row,
} from 'reactstrap';
import slugify from 'slugify';
import { setFormErrorsOrHandleError } from 'utils';
import * as Yup from 'yup';

function SlugInput(props) {
  const { children, defaultValue } = props;
  const [isDisabled, setIsDisabled] = useState(defaultValue);
  return children({ isDisabled, setIsDisabled });
}

const normalizeImageUrl = (image) => {
  if (typeof image === 'string') {
    return image.trim();
  }

  if (image && typeof image === 'object') {
    return (image.url || image.image || image.src || '').trim();
  }

  return '';
};

export const buildMarketImagesFromPayload = (details) => {
  if (!details || typeof details !== 'object') {
    return [];
  }

  const previewImage = normalizeImageUrl(details.previewImage);
  const galleryImages = (Array.isArray(details.images) ? details.images : [])
    .map(normalizeImageUrl)
    .filter(Boolean);

  const imageUrls = previewImage
    ? [previewImage, ...galleryImages.filter((url) => url !== previewImage)]
    : galleryImages;

  const marketId = details._id || details.id || 'market';

  return imageUrls.map((image, index) => ({
    id: `${marketId}-image-${index}`,
    image,
    checked: true,
  }));
};

const SLUGIFY_OPTIONS = {
  lower: true,
  locale: 'vi',
  replacement: '-',
};

const formatSlugInput = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return slugify(value, {
    ...SLUGIFY_OPTIONS,
    trim: false,
  });
};

const formatAddressProvinceOptions = (provinces) => {
  const list = Array.isArray(provinces) ? provinces : [];

  return list.map((province) => ({
    ...province,
    label: province.name,
    value: province.id,
    name: province.name,
  })).sort((a, b) => a.label.localeCompare(b.label));
};

function DiscoverMarketDetails({
  isCreate,
  marketId,
  marketDetails,
  callback,
}) {
  const [provinces, setProvinces] = useState([]);
  const [provincesLoading, setProvincesLoading] = useState(true);

  const details = useMemo(() => (isCreate ? {} : marketDetails), [
    isCreate,
    marketDetails,
  ]);

  const initialFormValues = useMemo(() => {
    const imagesFromDetails = buildMarketImagesFromPayload(details);

    return {
      name: details?.name || '',
      nameEN: details?.nameEN || '',
      description: details?.description || '',
      descriptionEN: details?.descriptionEN || '',
      slug: details?.slug || '',
      province: details?.province || details?.region || '',
      isPublished: details?.isPublished || false,
      isDisplayedOnHomepage: details?.isDisplayedOnHomepage || false,
      images: imagesFromDetails,
    };
  }, [details]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,

    validationSchema: Yup.object({
      slug: Yup
        .string()
        .required('Vui lòng nhập slug')
        .max(100, 'Slug không quá 100 ký tự'),
      name: Yup
        .string()
        .required('Vui lòng nhập tên thị trường')
        .min(1, 'Vui lòng nhập tên thị trường')
        .max(100, 'Tên thị trường không quá 100 ký tự'),
      nameEN: Yup
        .string()
        .required('Vui lòng nhập tên thị trường')
        .min(1, 'Vui lòng nhập tên thị trường')
        .max(100, 'Tên thị trường không quá 100 ký tự'),
      province: Yup
        .string()
        .required('Vui lòng chọn tỉnh thành phố')
        .max(100, 'Tỉnh thành phố không quá 100 ký tự'),
      description: Yup
        .string()
        .required('Vui lòng nhập mô tả')
        .min(1, 'Vui lòng nhập mô tả')
        .max(2000, 'Mô tả không quá 2000 ký tự'),
      descriptionEN: Yup
        .string()
        .required('Vui lòng nhập mô tả')
        .min(1, 'Vui lòng nhập mô tả')
        .max(2000, 'Mô tả không quá 2000 ký tự'),
      isDisplayedOnHomepage: Yup.boolean(),
      images: Yup
        .array()
        .max(1, 'Vui lòng chọn tối đa 1 ảnh'),
    }),

    onSubmit: async (values) => {
      const checkedImages = (values.images || [])
        .filter((item) => item.checked)
        .map((item) => item.image);

      const payload = {
        name: values.name,
        nameEN: values.nameEN,
        slug: formatSlugInput(values.slug).replace(/^-+|-+$/g, ''),
        province: values.province,
        ...(checkedImages.length > 0 && {
          previewImage: checkedImages[0],
          images: checkedImages.slice(1),
        }),
        description: values.description,
        descriptionEN: values.descriptionEN,
        isPublished: values.isPublished,
        isDisplayedOnHomepage: values.isDisplayedOnHomepage,
      };

      try {
        if (isCreate) {
          await marketApi.createMarket(payload);
        } else {
          await marketApi.updateMarket(marketId, payload);
        }

        if (isCreate) {
          await callback?.();
        } else {
          toast.success('Cập nhật thành công');
          await callback?.();
        }
      } catch (error) {
        setFormErrorsOrHandleError(error, validation);
      }
    },
  });

  const formattedProvinces = useMemo(
    () => formatAddressProvinceOptions(provinces),
    [provinces],
  );

  const selectedProvinceValue = useMemo(() => {
    const matchedProvince = formattedProvinces.find(
      (item) => item.name === validation.values.province,
    );

    return matchedProvince?.value ?? '';
  }, [formattedProvinces, validation.values.province]);

  const onChangeProvince = React.useCallback((selected) => {
    const matchedProvince = formattedProvinces.find(
      (item) => item.value === selected.value,
    );

    validation.setFieldValue(
      'province',
      matchedProvince?.name || selected.label || '',
      true,
    );
  }, [formattedProvinces, validation]);

  React.useEffect(() => {
    let cancelled = false;

    const fetchProvinces = async () => {
      try {
        setProvincesLoading(true);
        const response = await addressApi.getAddress({
          countries: COUNTRIES_API_PROVIDE.VIETNAM,
        });
        const list = response?.payload ?? response ?? [];

        if (!cancelled) {
          setProvinces(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) {
          setProvinces([]);
        }
      } finally {
        if (!cancelled) {
          setProvincesLoading(false);
        }
      }
    };

    fetchProvinces();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!isCreate) {
      return;
    }

    validation.setFieldValue(
      'slug',
      slugify(validation.values.name, SLUGIFY_OPTIONS),
      true,
    );
  }, [isCreate, validation.values.name]);

  return (
    <Row className="justify-content-center">
      <Col lg={12}>
        <Card className="p-3">
          <form onSubmit={validation.handleSubmit}>
            <FormField label="Tên thị trường - VI" isRequired>
              <InputValidation
                isCustomOnChange
                name="name"
                placeholder="Nhập tên"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={100}
                isShowCounter
              />
            </FormField>

            <FormField label="Tên thị trường - EN" isRequired>
              <InputValidation
                name="nameEN"
                placeholder="Nhập tên"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={100}
                isShowCounter
              />
            </FormField>

            <FormField label="Mô tả thị trường - VI" isRequired>
              <InputValidation
                type="textarea"
                name="description"
                placeholder="Nhập mô tả"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={2000}
                style={{ minHeight: '84px' }}
                isShowCounter
              />
            </FormField>

            <FormField label="Mô tả thị trường - EN" isRequired>
              <InputValidation
                type="textarea"
                name="descriptionEN"
                placeholder="Nhập mô tả"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={2000}
                style={{ minHeight: '84px' }}
                isShowCounter
              />
            </FormField>

            <FormField label="Slug" isRequired>
              <SlugInput defaultValue>
                {({ isDisabled, setIsDisabled }) => (
                  <div className="position-relative">
                    <InputValidation
                      name="slug"
                      type="text"
                      placeholder="Nhập slug"
                      validation={validation}
                      isCustomOnChange
                      onChange={(e) => {
                        validation.setFieldValue(
                          'slug',
                          formatSlugInput(e.target.value),
                        );
                      }}
                      isDisabled={isDisabled}
                      style={{ paddingRight: '30px' }}
                    />

                    <button
                      type="button"
                      onClick={() => setIsDisabled((prev) => !prev)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px 10px',
                      }}
                    >
                      <Pencil width={16} height={16} />
                    </button>
                  </div>
                )}
              </SlugInput>
            </FormField>

            <FormField label="Tỉnh thành phố" isRequired>
              <SelectValidation
                name="province"
                placeholder="Chọn tỉnh thành phố"
                options={formattedProvinces}
                validation={validation}
                value={selectedProvinceValue}
                onChange={onChangeProvince}
                isDisabled={provincesLoading}
                isHiddenSearchable
              />
            </FormField>

            <div className="mb-4">
              <AddImages
                multiple={false}
                value={validation.values.images}
                onChange={(images) =>
                  validation.setFieldValue('images', images, true)
                }
              />

              {validation.errors.images && validation.touched.images && (
                <FormFeedback type="invalid" className="d-block font-size-12">
                  {validation.errors.images}
                </FormFeedback>
              )}
            </div>

            <div className="d-flex align-items-center gap-4 mb-4">
              <label
                htmlFor="isPublished"
                className="d-flex align-items-center gap-2 mb-0 cursor-pointer"
              >
                <Input
                  name="isPublished"
                  id="isPublished"
                  type="checkbox"
                  checked={validation.values.isPublished}
                  onChange={(e) =>
                    validation.setFieldValue('isPublished', e.target.checked)
                  }
                />
                <span>Xuất bản</span>
              </label>

              <label
                htmlFor="isDisplayedOnHomepage"
                className="d-flex align-items-center gap-2 mb-0 cursor-pointer"
              >
                <Input
                  name="isDisplayedOnHomepage"
                  id="isDisplayedOnHomepage"
                  type="checkbox"
                  checked={validation.values.isDisplayedOnHomepage}
                  onChange={(e) =>
                    validation.setFieldValue(
                      'isDisplayedOnHomepage',
                      e.target.checked,
                    )
                  }
                />
                <span>Hiển thị trang chủ</span>
              </label>
            </div>

            <Button
              classes="w-100"
              type="submit"
              loading={validation.isSubmitting}
              disabled={validation.isSubmitting}
            >
              {isCreate ? 'Thêm' : 'Cập nhật'}
            </Button>
          </form>
        </Card>
      </Col>
    </Row>
  );
}

DiscoverMarketDetails.propTypes = {
  marketDetails: PropTypes.instanceOf(Object),
  marketId: PropTypes.string,
  isCreate: PropTypes.bool.isRequired,
  callback: PropTypes.func,
};

DiscoverMarketDetails.defaultProps = {
  marketDetails: {},
  callback: () => { },
  marketId: '',
};

export default DiscoverMarketDetails;
